import { IChartWidget } from './chart';
import { ITableWidget } from './table';
import { WidgetHeader } from './WidgetHeader';
import { IconType } from './types';
import { SpinningLogo } from '@/components/animations';
import { TremorTable } from './table/TremorTable';
import { TremorCompareTable } from './table/TremorCompareTable';
import { CompetitorTable } from './table/CompetitorTable';
import { BarChart } from './chart/BarChart';
import { LineChart } from './chart/LineChart';
import PieChart from './chart/PieChart';
import Funnel from './chart/Funnel';
import CompactTickerCard from './tickers/CompactTickerCard';
import { BudgetTickersGroupByProduct } from './tickers/BudgetTickersGroupByProduct';
import { useDashboard, Widget, widgetEndpoints } from '@/modules';
import { getPageDisplayTitle, isDisabledByIncompleteProducts } from '@/modules/pages/utils/products';
import { WidgetFactoryWrapper, RefineModeContext, useTableMapExport, useWidgetData } from './WidgetFactoryContext';
import classNames from 'classnames';
import { IColumnDef, ITableData } from './table/types';
import { useWidgetOperations } from '@/modules/widgets/hooks/useWidgetOperations';
import { useEditWidget } from '@/modules/widgets/context/EditWidgetContext';
import { StackBarChart } from './chart/StackBarChart';
import { StackedBarLine } from './chart/StackedBarLine';
import BubbleChart from './chart/BubbleChart';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NumberUtils } from '@/utils/NumberUtils';
import { useWidgetFiltersController } from './WidgetFilters/useWidgetFiltersController';
import { useWidgetProducts } from './WidgetFilters/useWidgetProducts';
import { getWidgetComparePolicy } from './WidgetFilters/compare-policy';
import { WidgetSubHeader } from './WidgetSubHeader';
import { DataSources } from './DataSources';
import { IframeWidget } from './iframe/IframeWidget';
import { SocialImpactPosts } from './social-impact-posts/SocialImpactPosts';
import { CreativeGallery } from './creative-gallery';
import { ConsumerDataWidget } from './consumer-data/ConsumerDataWidget';
import { ALLOW_EDIT_WIDGETS, ALLOW_INSIGHTS_GENERATION_WIDGETS } from '@/modules/widgets/utils/widgetCapabilities';
import { GHOST_WIDGETS } from '@/constants/widget';
import { NoDataView } from './NoDataView';
import { ErrorView } from './ErrorView';
import { formatWidgetDateRangeLabel, getDateRangeForPreset, formatDate } from '@/modules/widgets/utils/dateUtils';
import { useHiddenItems } from './chart/hooks/useHiddenItems';
import { DEFAULT_COLORS } from '@/constants/colors';
import { getDatesSegmentation } from '@/utils/segmentationUtils';
import { TopOffers } from './top-offers/TopOffers';
import { useNavigate } from '@/hooks/useNavigate';
import { LoadingSpinnerIcon } from '@/components/icons/LoadingSpinnerIcon';
import { CombinedWidget } from './combined';
import { ContentScheduleWidget } from './content-schedule';
import { IFilters } from './useWidgetResolver';
import { isColumnHidden, ColumnVisibilityContext } from './table/columnVisibility';
import { filterRowsWithAllZeroMetrics } from './table/utils';
import { COMPACTION_HIDDEN_TRIGGERS } from '@/components/organisms/GridSystem/config/grid-config';
import { useHideEmptyWidgets } from '@/hooks/useHideEmptyWidgets';


import TableMapOrganismWrapper from '@/migrated-widgets/table-map/components/organisms/TableMap';
import AudienceVisitorsOrganismWrapper from '@/migrated-widgets/table-map/components/organisms/AudienceVisitorsMap';
import { GetDealerContractedProductsResponse } from '@/modules/dealer';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

type WidgetProps = {
    widget: Widget;
    showDeleteButton?: boolean;
    onWidgetDelete?: () => void;
    onMinimizeChange?: (isMinimized: boolean) => void;
    onHiddenChange?: (hidden: boolean) => void;
    colSpan?: number;
    isDragging?: boolean;
    setDraggingDisabled: React.Dispatch<React.SetStateAction<boolean>>;
};

const getWidgetIconType = (widget?: Widget, data?: ITableWidget | IChartWidget | Widget): IconType => {

    if (widget?.type === 'table' || widget?.type === 'competitor-table') {
        return 'table';
    }

    if (widget?.type === 'combined') {
        return 'chart';
    }

    if (widget?.type === 'chart' && data) {
        const chartWidget = data as IChartWidget;
        switch (chartWidget.chartType) {
            case 'line':
                return 'line';
            case 'pie':
                return 'pie';
            case 'doughnut':
                return 'doughnut';
            case 'bar':
            case 'stack-bar':
            case 'stacked-bar-line':
            case 'radar':
            default:
                return 'chart';
        }
    }

    return 'chart';
};

interface WidgetFactoryComponentProps {
    onWidgetDelete?: () => void;
    showDeleteButton?: boolean;
    onMinimizeChange?: (isMinimized: boolean) => void;
    onHiddenChange?: (hidden: boolean) => void;
    colSpan?: number;
    isDragging?: boolean;
}

export const WidgetFactoryComponent = ({ onWidgetDelete = () => { }, showDeleteButton = true, onMinimizeChange, onHiddenChange, colSpan, isDragging }: WidgetFactoryComponentProps) => {
    const { widget, data, loading, exportLoading, error, updateWidget, fetchWidgetData, fetchWidgetExportData, noData, setNoData, retryFetch } = useWidgetData();
    const { triggerExportHandler } = useTableMapExport();

    const { state: {
        selectedDealer, selectedDateRange: selectedDashboardDateRange, selectedCompareEnabled: selectedDashboardCompareEnabled,
        selectedCompareDateRange: selectedDashboardCompareDateRange, dealerContractedProducts
    } } = useDashboard();
    const displayTitle = getPageDisplayTitle(widget?.metadata?.['widget-titles'], dealerContractedProducts?.contractedProducts) ?? widget?.title;
    const dynamicIconType = getWidgetIconType(widget, data);
    const heightClass = ((widget ?? {}).type === 'table' || (widget ?? {}).type === 'competitor-table') ? 'h-auto min-h-[16rem]' : 'h-full min-h-[16rem]';
    const { updateWidgetOperation } = useWidgetOperations();
    const { openEditDrawer } = useEditWidget();
    const widgetFiltersController = useWidgetFiltersController();
    const availableProducts = useWidgetProducts(widget);

    // When the user applies row-level data-filters that yield zero matches, we
    // keep the widget visible with a NoDataView + edit affordance instead of
    // letting the grid compact the slot away (which would leave no way back).
    const hasUserDataFilters = ((widget?.metadata as Record<string, unknown> | undefined)?.["data-filters"] as unknown[] | undefined)?.length ? true : false;
    const [isHovered, setIsHovered] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [sortConsumerData, setSortConsumerData] = useState<{ field: string; direction: 'asc' | 'desc' } | undefined>();
    const [hideEmptyWidgets] = useHideEmptyWidgets();
    const { hiddenItems: hiddenColumns, setHiddenItems: setHiddenColumns } = useHiddenItems();
    const { hiddenItems: hiddenMetrics, toggleItem: toggleMetric, setHiddenItems: setHiddenMetrics } = useHiddenItems();

    /** Stable fingerprint of server column defs (incl. hidden). Avoids re-sync when `data` is a new reference each render while still syncing real API updates. */
    const tableColumnHiddenSignature = useMemo(() => {
        if (widget?.type !== 'table' && widget?.type !== 'consumer-data') {
            return `__skip__:${widget?.id ?? ''}`;
        }
        const d = data as { widget?: { data?: { columns?: IColumnDef[] } }; data?: { columns?: IColumnDef[] }; columns?: IColumnDef[] } | null | undefined;
        const extracted = d?.widget?.data ?? d?.data ?? d;
        const cols = extracted?.columns;
        if (!Array.isArray(cols)) {
            return `${widget?.id ?? ''}:__nocols__`;
        }
        const body = cols
            .map((c: IColumnDef) => `${String(c.metric ?? '')}\0${String(c.header ?? '')}\0${c.hidden ? '1' : '0'}`)
            .join('|');
        const dynamic = Array.isArray((widget as any)?.metadata?.dynamicMetrics)
            ? ((widget as any).metadata.dynamicMetrics as string[]).join(',')
            : '';
        return `${widget?.id ?? ''}:${body}:${dynamic}`;
    }, [data, widget?.id, widget?.type, (widget as any)?.metadata?.dynamicMetrics]);

    useEffect(() => {
        if (widget?.type !== 'table' && widget?.type !== 'consumer-data') {
            setHiddenColumns(new Set());
            return;
        }
        const d = data as { widget?: { data?: { columns?: IColumnDef[] } }; data?: { columns?: IColumnDef[] }; columns?: IColumnDef[] } | null | undefined;
        const extracted = d?.widget?.data ?? d?.data ?? d;
        const cols = extracted?.columns;
        if (!Array.isArray(cols)) {
            setHiddenColumns(new Set());
            return;
        }
        // Hidden-by-default ("dynamic") metrics: columns whose metric/field/header is listed in
        // metadata.dynamicMetrics start hidden. Viewers reveal them via the table Columns dropdown.
        const dynamicMetrics: string[] = Array.isArray((widget as any)?.metadata?.dynamicMetrics)
            ? (widget as any).metadata.dynamicMetrics
            : [];
        const isDynamicColumn = (c: IColumnDef) =>
            dynamicMetrics.length > 0 &&
            (dynamicMetrics.includes(c.metric as string) ||
                dynamicMetrics.includes(c.field as string) ||
                dynamicMetrics.includes(c.header as string));
        const serverHidden = new Set(
            cols.filter((c) => c.hidden || isDynamicColumn(c)).map((c) => c.header).filter((h): h is string => Boolean(h)),
        );
        setHiddenColumns(serverHidden);
    }, [tableColumnHiddenSignature]);

    const navigate = useNavigate();

    const hasClickAction = Boolean(widget?.metadata?.['click-action']?.pageId);
    const clickActionPageId = widget?.metadata?.['click-action']?.pageId;

    const handleWidgetClick = (e: React.MouseEvent) => {
        if (hasClickAction && clickActionPageId) {
            e.stopPropagation();
            navigate(`/dashboard?pageId=${clickActionPageId}`);
        }
    };

    const allowInsights = widget?.type ? ALLOW_INSIGHTS_GENERATION_WIDGETS.includes(widget.type) : false;
    const allowEdit = widget?.type ? ALLOW_EDIT_WIDGETS.includes(widget.type) : false;

    const widgetDatePeriod = widget?.datePeriod;
    const widgetStartDate = (widget as any)?.service?.parameters?.startDate;
    const widgetEndDate = (widget as any)?.service?.parameters?.endDate;

    const presetRange = widgetDatePeriod ? getDateRangeForPreset(widgetDatePeriod) : null;

    const widgetDateRangeLabel =
        widgetDatePeriod === "dashboard_date"
            ? null
            : !widgetDatePeriod || widgetDatePeriod === "custom"
                ? formatWidgetDateRangeLabel(widgetStartDate, widgetEndDate)
                : presetRange
                    ? formatWidgetDateRangeLabel(
                        formatDate(presetRange!.from),
                        formatDate(presetRange!.to),
                    )
                    : null;

    const isAIWidget = Boolean(widget?.prompt);
    const aiDateRangeLabel = (widgetDateRangeLabel && (widgetDatePeriod !== "dashboard_date" || isAIWidget)) ? widgetDateRangeLabel : undefined;


    const isTicker = widget?.type === 'ticker' || widget?.type === 'budget-tickers-group-by-product';
    const wrapperClassName = classNames(
        {
            // Non-ticker widgets: show standard border; tickers rely on inner Card border
            'border': !isTicker,
            'border-blue-300 opacity-30': isDragging && !isTicker,
            'border-[#c9d1d4]': !isDragging && !isTicker,
        }
    )


    const handleMinimizeToggle = () => {
        const newMinimizedState = !isMinimized;
        setIsMinimized(newMinimizedState);
        onMinimizeChange?.(newMinimizedState);
    };

    const hasManualFilters = widgetFiltersController.hasUserInteractedWithFilters;
    const debouncedSelectedDimensionItems = useDebouncedValue(widgetFiltersController.selectedDimensionItems, 500);

    const dimensionRefetchSignature = debouncedSelectedDimensionItems.map((i) => i.text).join("|");

    const isWidgetEmpty = useMemo(() => {
        if (!widget) return false;
        // Self-fetching widgets manage their own empty-state rendering; never mark them hidden here.
        if (widget.type === 'content-schedule') return false;
        // Hide widget if any of its required products has BuildOutStatus === "IN"
        if (isDisabledByIncompleteProducts(widget.metadata?.['hide-on-incomplete-buildout'], dealerContractedProducts?.incompleteProducts ?? [])) {
            return true;
        }
        // Hide widget unless any of its required products has BuildOutStatus === "IN"
        if (widget.metadata?.['show-on-incomplete-buildout'] !== undefined) {
            if (!isDisabledByIncompleteProducts(widget.metadata['show-on-incomplete-buildout'], dealerContractedProducts?.incompleteProducts ?? [])) {
                return true;
            }
        }
        if (!(dealerContractedProducts?.contractedProducts ?? []).length && dealerContractedProducts?.contractedProductsBudget) return true;
        if (loading) return false;
        if (error) return false;
        if (!data) return true;

        const extracted = (data as any)?.widget?.data || (data as any)?.data || data;


        switch (widget.type) {
            case 'chart': {
                const series = extracted?.data;
                if (!Array.isArray(series) || series.length === 0) return true;
                // Pie/doughnut/funnel/bubble emit per-row `value` instead of keying by metric name.
                const chartType = (widget as any).chartType;
                if (chartType === 'pie' || chartType === 'doughnut' || chartType === 'funnel' || chartType === 'bubble') {
                    return !series.some((row: any) => row?.value !== undefined && row?.value !== null && row?.value !== 0);
                }
                const metrics = (widget as any).metrics ?? [];
                if (metrics.length === 0) return false;
                // Line charts treat all-zero data as empty (matches LineChart component's hasMeaningfulData check).
                const isLine = chartType === 'line';
                const indexKey = extracted?.metadata?.indexKey;
                const valueMetrics = metrics.filter((m: string) => m !== indexKey);
                const treatZeroAsEmpty = chartType === 'line' || chartType === 'stack-bar' || chartType === 'stacked-bar-line';
                const hasAnyMetricValue = series.some((row: any) =>
                    valueMetrics.some((m: string) => {
                        const v = row?.[m];
                        if (v === undefined || v === null) return false;
                        if (treatZeroAsEmpty && v === 0) return false;
                        return true;
                    })
                );
                return !hasAnyMetricValue;
            }
            case 'table': {
                const rows = extracted?.rows;
                if (!Array.isArray(rows) || rows.length === 0 || extracted?.error) return true;
                const columns = extracted?.columns;
                if (!columns) return false;
                return filterRowsWithAllZeroMetrics(rows, columns).length === 0;
            }
            case 'competitor-table': {
                const rows = extracted?.rows;
                return !Array.isArray(rows) || rows.length === 0;
            }
            case 'top-offers': {
                const offers = extracted?.offers;
                return !Array.isArray(offers) || offers.length === 0;
            }
            case 'social-impact-posts': {
                const posts = extracted?.posts;
                return !Array.isArray(posts) || posts.length === 0;
            }
            case 'creative-gallery': {
                const items = extracted?.items;
                const videos = extracted?.videos;
                const itemsEmpty = !Array.isArray(items) || items.length === 0;
                const videosEmpty = !Array.isArray(videos) || videos.length === 0;
                return itemsEmpty && videosEmpty;
            }
            case 'consumer-data': {
                const data = extracted?.rows;
                return !Array.isArray(data) || data.length === 0;
            }
            case 'iframe': {
                const iframeUrl = extracted?.iframeUrl;
                return !iframeUrl;
            }
            case 'ticker': {
                if ((data as any)?.error) return true;
                const rawValue = extracted?.value;
                if (rawValue === undefined || rawValue === null) return true;
                const numeric = typeof rawValue === 'number'
                    ? rawValue
                    : Number(String(rawValue).replace(/[^\d.-]/g, ''));
                return !numeric || Number.isNaN(numeric);
            }
            case 'data-sources': {
                const data = extracted;
                return !Array.isArray(data) || data.length === 0;
            }
            case 'combined': {
                if (!Array.isArray(extracted) || extracted.length === 0) return true;
                return extracted.every((section: any) => {
                    const chartData = section?.data;
                    const tableRows = section?.rows;
                    const chartEmpty = !Array.isArray(chartData) || chartData.length === 0;
                    const tableEmpty = !Array.isArray(tableRows) || tableRows.length === 0;
                    return chartEmpty && tableEmpty;
                });
            }
            default:
                return false;
        }
    }, [data, error, loading, widget, selectedDealer, dealerContractedProducts]);

    // True when the user has narrowed the product filter to a subset that yields no data
    // but there is more than one product to pick from — drives the placeholder below that
    // lets the user reset or pick a different product instead of the generic "No Data" view.
    const isProductFilterNarrowing = useMemo(() => (
        Boolean(widget?.filterByProduct) &&
        availableProducts.length > 1 &&
        widgetFiltersController.selectedProducts.length > 0 &&
        widgetFiltersController.selectedProducts.length < availableProducts.length
    ), [widget?.filterByProduct, availableProducts.length, widgetFiltersController.selectedProducts.length]);

    // Narrowing the product filter must not let the grid compact the slot away — treat
    // it like user data-filters do (the placeholder below handles the empty result).
    const shouldHideWidget = useMemo(() => isWidgetEmpty && !hasManualFilters && !isProductFilterNarrowing, [widget, isWidgetEmpty, hasManualFilters, isProductFilterNarrowing]);

    useEffect(() => {
        if (loading) {
            setNoData(false);
            return;

        }
        if (shouldHideWidget) {
            setNoData(true);
        }
    }, [setNoData, shouldHideWidget, loading]);

    useEffect(() => {
        const hideForError = COMPACTION_HIDDEN_TRIGGERS.errorState && !!error;
        const hideForEmpty = hideEmptyWidgets && COMPACTION_HIDDEN_TRIGGERS.emptyData && (shouldHideWidget || noData) && !hasUserDataFilters && !isProductFilterNarrowing;
        onHiddenChange?.((hideForError || hideForEmpty) && !loading);
    }, [shouldHideWidget, noData, loading, error, hasUserDataFilters, isProductFilterNarrowing, hideEmptyWidgets]);


    const handlePrevenEvent = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    }

    const buildFetchData = (fetchData: (filters?: IFilters, widgetOverride?: Widget) => Promise<void>) => {
        if (widget && data?.data) {
            const productsFilter = widgetFiltersController.selectedProducts ?? [];
            const serviceFilters = widgetFiltersController.selectedServiceFilters ?? {};
            // const periodFilter = widgetFiltersController.selectedPeriodItems ?? []
            const dateRangeFilter = widgetFiltersController.selectedDateRange ?? null;
            const compareDateRangeFilter = widgetFiltersController.selectedCompareDateRange ?? null;
            const selectedCompareEnabled = widgetFiltersController.selectedCompareEnabled ?? false;
            const consumerDataFilters = widgetFiltersController.consumerDataFilters ?? {};
            const policy = getWidgetComparePolicy(widget, widgetFiltersController.selectedPeriodItems);

            const multiFilterConfigs = (data as any)?.data?.metadata?.filters as
                Array<{ metric: string; items: string[] }> | undefined;
            const dimensionFilters: { metric: string; values: string[] }[] = [];
            if (multiFilterConfigs?.length) {
                for (const config of multiFilterConfigs) {
                    const selected = widgetFiltersController.selectedFilters[config.metric] ?? [];
                    if (selected.length > 0 && selected.length < config.items.length) {
                        dimensionFilters.push({ metric: config.metric, values: selected.map((i) => i.text) });
                    }
                }
            } else if (debouncedSelectedDimensionItems.length > 0) {
                const dimName = (data as any)?.data?.metadata?.filter?.dimension?.metricName as string | undefined;
                if (dimName) {
                    dimensionFilters.push({
                        metric: dimName,
                        values: debouncedSelectedDimensionItems.map((i) => i.text),
                    });
                }
            }

            const serviceFiltersConfig = (data as any)?.data?.metadata?.["service-filters"] ?? (data as any)?.metadata?.["service-filters"];
            const selectionModeByParam = (Array.isArray(serviceFiltersConfig) ? serviceFiltersConfig : []).reduce<Record<string, "single" | "multi">>(
                (acc, f: any) => {
                    const paramName = String(f?.paramName ?? "");
                    if (!paramName) return acc;
                    acc[paramName] = f?.selectionMode === "single" ? "single" : "multi";
                    return acc;
                },
                {},
            );

            const serviceFiltersParams = Object.entries(serviceFilters).reduce<Record<string, any>>((acc, [paramName, items]) => {
                const values = (items ?? []).map((it: any) => it?.data?.value).filter((v: any) => v !== undefined && v !== null && String(v) !== "");
                if (values.length > 0) {
                    const selectionMode = selectionModeByParam[paramName] ?? "multi";
                    acc[paramName] = selectionMode === "single" ? values[0] : values;
                }
                return acc;
            }, {});

            // Always override datePeriod if period selector is enabled and user has made a selection
            if (widget.showPeriodSelector && dateRangeFilter) {
                const isTimeSeriesChart =
                    widget.type === "chart" &&
                    (widget.chartType === "line" || widget.chartType === "bar" || widget.chartType === "stack-bar" || widget.chartType === "stacked-bar-line");

                let widgetForApiCall;

                if (isTimeSeriesChart) {
                    const fixedSegment = widget.metadata?.['fixed-segment'] === true;
                    const segment = fixedSegment
                        ? (widget.service?.parameters?.segment ?? "month")
                        : (dateRangeFilter ? getDatesSegmentation(dateRangeFilter.from, dateRangeFilter.to) : "month");

                    widgetForApiCall = {
                        ...(widget as any),
                        filters: {
                            ...(widget as any).filters,
                            dateRange: dateRangeFilter,
                            compare: selectedCompareEnabled && compareDateRangeFilter
                                ? {
                                    compare: {
                                        enabled: true,
                                        dateRange: compareDateRangeFilter,
                                    },
                                }
                                : undefined,
                        },
                        datePeriod: "dashboard_date",
                        service: {
                            ...widget.service,
                            parameters: {
                                ...widget.service?.parameters,
                                segment: segment,
                            },
                            dataSources: widget.service?.dataSources?.map((datasource: any) => ({
                                ...datasource,
                                parameters: {
                                    ...datasource.parameters,
                                    segment: segment,
                                },
                            })),
                        },
                    };
                } else {
                    widgetForApiCall = {
                        ...widget,
                        ...(widget.type === "consumer-data" && sortConsumerData ? { exportMetadata: { ...(widget as any).exportMetadata, sortBy: sortConsumerData?.field, sortDirection: sortConsumerData?.direction } } : {}),
                        datePeriod: "dashboard_date",
                        filters: {
                            ...(widget as any).filters,
                            dateRange: dateRangeFilter,
                            compare: selectedCompareEnabled && compareDateRangeFilter
                                ? {
                                    compare: {
                                        enabled: true,
                                        dateRange: compareDateRangeFilter,
                                    },
                                }
                                : undefined,
                        }
                    };
                }

                fetchData(
                    {
                        products: productsFilter.map((el) => el?.data?.productId ?? 0) as number[],
                        dateRange: dateRangeFilter ?? undefined,
                        serviceFilters: serviceFiltersParams,
                        compare: selectedCompareEnabled && compareDateRangeFilter
                            ?
                            {
                                enabled: true,
                                dateRange: compareDateRangeFilter,
                            }
                            : undefined,
                        ...(dimensionFilters.length ? { dimensionFilters } : {}),
                    },
                    widgetForApiCall,
                );
            } else {
                const widgetFiltersForApiCall: any =
                    widget.type === "consumer-data"
                        ? { dateRange: dateRangeFilter ?? (widget as any)?.filters?.dateRange, offset: consumerDataFilters.offset ?? 0 }
                        : {
                            products: productsFilter.map((el) => el?.data?.productId ?? 0) as number[],
                            dateRange: selectedDashboardDateRange ?? undefined,
                            serviceFilters: serviceFiltersParams,
                            ...(selectedDashboardCompareEnabled && selectedDashboardCompareDateRange && !policy.skipPageCompare
                                ? {
                                    compare: {
                                        enabled: true,
                                        dateRange: selectedDashboardCompareDateRange,
                                    },
                                }
                                : {}),
                            ...(dimensionFilters.length ? { dimensionFilters } : {}),
                        };

                const consumerDataWidgetForApiCall =
                    widget.type === "consumer-data"
                        ? ({
                            ...widget,
                            exportMetadata: { ...(widget as any).exportMetadata, sortBy: sortConsumerData?.field, sortDirection: sortConsumerData?.direction },
                            service: {
                                ...widget.service,
                                parameters: {
                                    ...(widget.service?.parameters as any),
                                    ...(consumerDataFilters.marketingScores ? { marketingScores: consumerDataFilters.marketingScores } : {}),
                                    ...(consumerDataFilters.eventTypes ? { eventTypes: consumerDataFilters.eventTypes } : {}),
                                    // Explicit (not conditional-spread) so clearing back to "any" or
                                    // dropping below 2 selected activities overwrites a stale "all"
                                    // left over from a previous selection, instead of leaking forward.
                                    eventTypesMode: consumerDataFilters.eventTypesMode,
                                    limit: consumerDataFilters.limit ?? 10,
                                    offset: consumerDataFilters.offset ?? 0,
                                },
                            },
                            filters: {
                                ...(widget as any).filters,
                                dateRange: selectedDashboardDateRange,
                                offset: consumerDataFilters.offset ?? 0,
                            },
                        } as Widget)
                        : undefined;

                fetchData(widgetFiltersForApiCall, consumerDataWidgetForApiCall);
            }
        }
    };

    // * REFETCH DATA WHEN FILTERS CHANGE
    useEffect(() => {
        buildFetchData(fetchWidgetData);
    }, [widgetFiltersController.selectedProducts, widgetFiltersController.selectedServiceFilters, widgetFiltersController.selectedPeriodItems, selectedDashboardDateRange, widgetFiltersController.selectedDateRange, widgetFiltersController.selectedCompareEnabled, widgetFiltersController.selectedCompareDateRange, widgetFiltersController.consumerDataFilters, widgetFiltersController.selectedFilters, dimensionRefetchSignature]);

    const updatedWidget = useMemo(() => widget?.updatedAt, [widget])

    useEffect(() => {
        console.log('Widget updated:', updatedWidget);
        setHiddenMetrics(new Set());
    }, [updatedWidget])

    const handleColumnReorder = (_newColumns: IColumnDef[]) => {
        // Per MS-17014: Column reorder is local only — no backend save
    };

    const handleColumnChangeVisibility = (columns: IColumnDef[]) => {
        const newHiddenColumns = new Set<string>();
        for (const col of columns) {
            if (col.hidden) {
                newHiddenColumns.add(col.header);
            }
        }
        setHiddenColumns(newHiddenColumns);
    };

    // Fields hidden purely by config — widget.metadata['hide-columns'] and
    // widget.metadata['column-visibility'] (e.g. paidSocialViews hidden unless the dealer has
    // Facebook/Facebook Lead Ads/Facebook Dynamic Ads contracted). These are keyed by field name
    // directly in the metadata, so this doesn't need the fetched row/column data at all — it's
    // known as soon as the widget config + dealerContractedProducts are available, before the
    // first get-widget-data call ever goes out.
    const configHiddenFields = useMemo(() => {
        const hideColumnsList: string[] = Array.isArray((widget as any)?.metadata?.['hide-columns'])
            ? (widget as any).metadata['hide-columns']
            : [];
        const fields = new Set<string>(hideColumnsList);

        const visibilityRules = (widget as any)?.metadata?.['column-visibility'];
        if (visibilityRules) {
            const columnVisibilityContext: ColumnVisibilityContext = {
                widgetProduct: widgetFiltersController?.selectedProducts?.map(p => p.data.productId) ?? [],
                widgetProductName: widgetFiltersController?.selectedProducts?.map(p => p.data.productName) ?? [],
                dealerProducts: dealerContractedProducts?.contractedProducts ?? [],
            };
            for (const field of Object.keys(visibilityRules)) {
                if (isColumnHidden(field, visibilityRules, columnVisibilityContext)) {
                    fields.add(field);
                }
            }
        }

        return Array.from(fields);
    }, [widget, widgetFiltersController?.selectedProducts, dealerContractedProducts]);

    // Fields hidden by the user manually unchecking a column in the Columns dropdown
    // (hiddenColumns is keyed by header — resolve to field/metric names here).
    const manualHiddenFields = useMemo(() => {
        if (!hiddenColumns || hiddenColumns.size === 0) return [] as string[];
        const extractedData = (data as any)?.widget?.data || (data as any)?.data || data;
        const columns = extractedData?.columns;
        if (!Array.isArray(columns)) return [] as string[];
        const fields: string[] = [];
        for (const col of columns) {
            if (hiddenColumns.has(col.header)) {
                const fieldName = col.field || col.metric;
                if (fieldName) fields.push(fieldName);
            }
        }
        return fields;
    }, [data, hiddenColumns]);

    const effectiveHiddenFields = useMemo(
        () => Array.from(new Set([...configHiddenFields, ...manualHiddenFields])),
        [configHiddenFields, manualHiddenFields]
    );

    // Debounce the hiddenFields refetch so toggling several columns in quick succession
    // (e.g. unchecking 3 boxes in the Columns dropdown), or a dealerContractedProducts/widget
    // update that changes which config-hidden columns apply, collapses into a single
    // get-widget-data call instead of firing one per change.
    const hiddenFieldsRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastSentHiddenFieldsSignatureRef = useRef<string | null>(null);
    const isFirstHiddenFieldsRunRef = useRef(true);

    useEffect(() => {
        return () => {
            if (hiddenFieldsRefetchTimerRef.current) {
                clearTimeout(hiddenFieldsRefetchTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const signature = JSON.stringify([...effectiveHiddenFields].sort());
        const isFirstRun = isFirstHiddenFieldsRunRef.current;
        isFirstHiddenFieldsRunRef.current = false;
        if (lastSentHiddenFieldsSignatureRef.current === signature) return;
        lastSentHiddenFieldsSignatureRef.current = signature;
        // On mount, if there's nothing to hide, skip the refetch entirely — the initial
        // widget-data fetch already went out with no hiddenFields, so there's nothing new
        // to tell the backend. Without this, every widget fired a redundant fetch ~500ms
        // after mounting even with zero hidden fields, which aborted the fetch already in
        // flight and re-triggered it — harmless for a single widget, but a visible wave of
        // canceled requests when a whole page's worth of widgets mount at once (e.g.
        // switching dashboard pages).
        if (isFirstRun && effectiveHiddenFields.length === 0) return;

        if (hiddenFieldsRefetchTimerRef.current) {
            clearTimeout(hiddenFieldsRefetchTimerRef.current);
        }
        hiddenFieldsRefetchTimerRef.current = setTimeout(() => {
            fetchWidgetData?.({ hiddenFields: effectiveHiddenFields } as IFilters, widget);
        }, 500);
        // widget/fetchWidgetData intentionally omitted: this should only re-fire when the
        // *set of hidden fields* actually changes, not on every widget/data reference change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [effectiveHiddenFields]);

    const handleMetricChangeVisibility = (metricName: string) => {
        toggleMetric(metricName);
    };

    const chartMetrics = useMemo(() => {
        if (widget?.type !== 'chart' || !data) return [];
        const extractedData = (data as any)?.widget?.data || (data as any)?.data || data;
        if (!extractedData?.data?.length) return [];

        const colors = widget.colors || DEFAULT_COLORS;
        const widgetLevel = widget.service?.parameters?.level || 'client';
        const chartType = widget.chartType || (data as any)?.chartType || extractedData?.chartType;
        let categories: string[] = [];
        let dataKeys: string[] = [];

        if (chartType === 'line') {
            if (widgetLevel === 'client' && extractedData?.metrics?.length) {
                categories = extractedData.metrics.map((m: any) => m.showName || m.metricName);
                dataKeys = extractedData.metrics.map((m: any) => m.metricName);
            } else if ((extractedData?.metrics?.length ?? 0) > 1) {
                // Multi-metric channel mode: use showName for display
                categories = extractedData.metrics.map((m: any) => m.showName || m.metricName);
                dataKeys = extractedData.metrics.map((m: any) => m.metricName);
            } else {
                // Single-metric channel mode: data keys are channel names
                const firstItem = extractedData.data[0];
                const keys = Object.keys(firstItem).filter((key: string) => key !== 'segment');
                categories = [...keys].sort((a: string, b: string) => a.localeCompare(b));
                dataKeys = [...keys].sort((a: string, b: string) => a.localeCompare(b));
            }
        } else if (chartType === 'bar' && extractedData?.metrics?.length) {
            categories = extractedData.metrics.map((m: any) => m.showName || m.metricName);
            dataKeys = extractedData.metrics.map((m: any) => m.metricName);
        } else if (chartType === 'stack-bar') {
            if (extractedData?.categories) {
                categories = extractedData.categories;
                dataKeys = extractedData.categories;
            } else {
                const indexKey = extractedData?.metadata?.indexKey || 'date';
                const firstItem = extractedData.data[0];
                const keys = Object.keys(firstItem).filter((key: string) => key !== indexKey);
                categories = keys;
                dataKeys = keys;
            }
        } else if (chartType === 'pie' || chartType === 'doughnut') {
            categories = extractedData.data.map((item: any) => item.name);
            dataKeys = extractedData.data.map((item: any) => item.name);
        } else if (chartType === 'funnel') {
            categories = extractedData.data.map((item: any) => item.label);
            dataKeys = extractedData.data.map((item: any) => item.label);
        } else if (chartType === 'bubble') {
            categories = extractedData.data.map((item: any) => item.label);
            dataKeys = extractedData.data.map((item: any) => item.label);
        } else if (chartType === 'stacked-bar-line' && extractedData?.metrics?.length) {
            categories = extractedData.metrics.map((m: any) => m.showName || m.metricName);
            dataKeys = extractedData.metrics.map((m: any) => m.metricName);
        } else {
            const firstItem = extractedData.data[0];
            const keys = Object.keys(firstItem).filter((key: string) => key !== 'segment');
            categories = keys;
            dataKeys = keys;
        }

        return categories.map((cat, idx) => ({
            name: cat,
            dataKey: dataKeys[idx],
            color: colors[idx % colors.length]
        }));
    }, [widget, data]);

    const chartCategories = useMemo(() => ({
        categories: chartMetrics.map(m => m.name),
        metricNames: chartMetrics.map(m => m.dataKey)
    }), [chartMetrics]);

    if (!widget) return null;

    // Self-rendering widgets bypass the generic factory pipeline entirely.
    if (widget.type === 'content-schedule') {
        return <ContentScheduleWidget widget={widget} showDeleteButton={showDeleteButton} onWidgetDelete={onWidgetDelete} onMinimizeChange={onMinimizeChange} colSpan={colSpan} />;
    }

    const showExportButton = ['table', 'competitor-table', 'consumer-data', 'table-map', 'audience-map'].includes(widget.type) || (widget.type === 'chart' && ['line', 'bar', 'pie', 'doughnut'].includes(widget.chartType));

    const onExportClick = () => {
        if (widget.type === 'table-map' || widget.type === 'audience-map') {
            const widgetId = String(widget.id);
            const handled = triggerExportHandler(widgetId);
            if (handled) {
                return;
            }
        }
        buildFetchData(fetchWidgetExportData);
    }

    // If it's truly empty for the selected date range (and the user didn't manually filter),
    // we hide the widget via onNoData() and let the wrapper render the placeholder.
    // When user-applied data-filters are the cause, keep the widget visible with an
    // edit affordance so they can revise or remove the filter that zeroed the result.
    if ((shouldHideWidget || noData) && !loading) {
        if (hasUserDataFilters) {
            return (
                <div
                    onDrag={handlePrevenEvent}
                    onDragOver={handlePrevenEvent}
                    onDrop={handlePrevenEvent}
                    className={classNames(
                        "bg-white rounded-[16px] flex-1 flex flex-col transition-all duration-300",
                        heightClass,
                        wrapperClassName,
                    )}
                >
                    {(displayTitle ?? widget?.title) && (
                        <h2
                            className="text-lg font-semibold text-widget-title truncate px-6 pt-6"
                            style={{ fontFamily: "Roboto, sans-serif" }}
                            title={displayTitle ?? widget?.title}
                        >
                            {displayTitle ?? widget?.title}
                        </h2>
                    )}
                    <div className="flex-1 flex items-center justify-center p-4">
                        <NoDataView
                            message="No matches for your filters"
                            description={
                                <p className="text-gray-500 text-center text-sm" style={{ fontFamily: "Roboto, sans-serif" }}>
                                    The active data filters returned zero rows for this date range.
                                </p>
                            }
                            action={{ label: "Edit filters", onClick: () => openEditDrawer(widget) }}
                        />
                    </div>
                </div>
            );
        }
        if (!hideEmptyWidgets) {
            return (
                <div
                    onDrag={handlePrevenEvent}
                    onDragOver={handlePrevenEvent}
                    onDrop={handlePrevenEvent}
                    className="bg-white rounded-[16px] w-full flex flex-col transition-all duration-300 border border-[#c9d1d4] min-h-[80px]"
                >
                    {(displayTitle ?? widget?.title) && (
                        <h2
                            className="text-sm font-semibold text-widget-title truncate px-4 pt-4"
                            style={{ fontFamily: "Roboto, sans-serif" }}
                            title={displayTitle ?? widget?.title}
                        >
                            {displayTitle ?? widget?.title}
                        </h2>
                    )}
                    <div className="flex-1 flex items-center justify-center p-4">
                        <NoDataView message="No data" description="No data available for this date range." />
                    </div>
                </div>
            );
        }
        return <div
            onDrag={handlePrevenEvent}
            onDragOver={handlePrevenEvent}
            onDrop={handlePrevenEvent}
            className={classNames(
                'flex-1 h-full p-4 flex items-center justify-center rounded-lg transition-all duration-200 ease-in-out ',
                ""
            )}
        ></div>;
    }

    // When the user narrows the product filter to a subset that yields no data
    // (and there is more than one product to pick from), keep the widget visible
    // with a placeholder that points back to the product filter — mirrors the
    // existing data-filters placeholder above.
    if (isWidgetEmpty && isProductFilterNarrowing && !loading) {
        return (
            <div
                onDrag={handlePrevenEvent}
                onDragOver={handlePrevenEvent}
                onDrop={handlePrevenEvent}
                className={classNames(
                    "bg-white rounded-[16px] flex-1 flex flex-col transition-all duration-300",
                    heightClass,
                    wrapperClassName,
                )}
            >
                {(displayTitle ?? widget?.title) && (
                    <h2
                        className="text-lg font-semibold text-widget-title truncate px-6 pt-6"
                        style={{ fontFamily: "Roboto, sans-serif" }}
                        title={displayTitle ?? widget?.title}
                    >
                        {displayTitle ?? widget?.title}
                    </h2>
                )}
                <div className="flex-1 flex items-center justify-center p-4">
                    <NoDataView
                        message="No data for the current product selection"
                        description={
                            <p className="text-gray-500 text-center text-sm" style={{ fontFamily: "Roboto, sans-serif" }}>
                                Try a different product, or show all.
                            </p>
                        }
                        action={{ label: "Show all products", onClick: () => widgetFiltersController.setSelectedProducts(availableProducts) }}
                    />
                </div>
            </div>
        );
    }


    // Compact ticker widgets always render at content height regardless of loading/data state
    if (widget.type === 'ticker' || widget.type === 'budget-tickers-group-by-product') {
        const widgetWithError = { ...widget, error: (data as any)?.error || (widget as any).error };

        const compactContent = widget.type === 'ticker'
            ? (
                <CompactTickerCard
                    data={(data as any)?.data ?? (data as any) ?? { value: 0, changeType: 'positive' }}
                    title={displayTitle}
                    dateRangeLabel={aiDateRangeLabel}
                    widget={widgetWithError}
                    onWidgetDelete={onWidgetDelete}
                    colors={widget.colors}
                />
            ) : (() => {
                const extractedData = (data as any)?.widget?.data || (data as any)?.data || data;
                return (
                    <BudgetTickersGroupByProduct
                        tickers={(extractedData as any)?.tickers}
                        widget={widgetWithError}
                        onWidgetDelete={onWidgetDelete}
                    />
                );
            })();

        return (
            <RefineModeContext.Consumer>
                {({ isRefineModeActive, setIsRefineModeActive }) => (
                    <div className={classNames("relative h-full", wrapperClassName)}>
                        {compactContent}
                        {loading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white/40">
                                <LoadingSpinnerIcon />
                            </div>
                        )}
                    </div>
                )}
            </RefineModeContext.Consumer>
        );
    }



    // When the errorState trigger is enabled, treat errored widgets the same
    // as empty ones: render the blank slot and let the compactor collapse the
    // gap. Flip COMPACTION_HIDDEN_TRIGGERS.errorState to false in grid-config
    // to restore the ErrorView card path below.
    if (error && COMPACTION_HIDDEN_TRIGGERS.errorState && !loading) {
        return <div
            onDrag={handlePrevenEvent}
            onDragOver={handlePrevenEvent}
            onDrop={handlePrevenEvent}
            className={classNames(
                'flex-1 h-full p-4 flex items-center justify-center rounded-lg transition-all duration-200 ease-in-out ',
                ""
            )}
        ></div>;
    }

    // For non-ticker widgets, handle error and loading states as before
    if (error) {
        return (
            <div
                className={classNames(
                    'bg-white rounded-[16px] flex-1 flex flex-col transition-all duration-300',
                    isMinimized ? 'h-fit' : heightClass,
                    hasClickAction && isHovered && 'border border-[#449995] cursor-pointer shadow-lg',
                    wrapperClassName
                )}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                onClick={handleWidgetClick}
            >
                <WidgetHeader
                    widget={widget}
                    fullyAIGenerated={data?.fullyAIGenerated}
                    title={displayTitle ?? widget?.title}
                    dateRangeLabel={aiDateRangeLabel}
                    description={widget.description}
                    widgetInfo={widget.widgetInfo}
                    iconType={dynamicIconType}
                    showDeleteButton={showDeleteButton}
                    onWidgetDelete={onWidgetDelete}
                    widgetData={data}
                    isHovered={isHovered}
                    isMinimized={isMinimized}
                    onMinimizeToggle={handleMinimizeToggle}
                    colSpan={colSpan}
                    allowInsights={allowInsights}
                    allowEdit={allowEdit}

                    widgetFiltersController={widgetFiltersController}
                    onColumnReorder={handleColumnReorder}
                    onColumnChangeVisibility={handleColumnChangeVisibility}
                    hiddenColumns={hiddenColumns}
                />

                {!isMinimized && !loading && (
                    <ErrorView onRetry={retryFetch} />
                )}
                {
                    loading && (
                        <div className="flex items-center justify-center flex-1 py-20 absolute z-100 h-full w-full top-0 left-0 bg-white/40">
                            <LoadingSpinnerIcon />
                        </div>
                    )
                }
            </div>
        );
    }

    if (GHOST_WIDGETS.includes(widget?.type)) {
        return (<div className={classNames('transition-all', 'duration-300 ease-in-out h-full flex justify-center items-center flex-col', { 'flex-1': widget.type !== 'table', 'min-h-[150px]': loading })}>
            {loading && (<>
                <div className={classNames("absolute h-full w-full top-0 left-0 ")}></div>

                <div className="flex items-center justify-center flex-1 py-20 absolute h-full z-100 w-full top-0 left-0 bg-white/40">
                    <LoadingSpinnerIcon />
                </div>
            </>
            )}


            {data && widget && renderWidget(widget, data, handleColumnReorder, showDeleteButton, onWidgetDelete, widgetFiltersController, colSpan, selectedDashboardCompareEnabled, hiddenColumns, hiddenMetrics, chartCategories, fetchWidgetData, loading, selectedDashboardDateRange, selectedDashboardCompareDateRange, setSortConsumerData, displayTitle, dealerContractedProducts)}
        </div>)
    }
    return (
        <div
            className={classNames(
                'bg-white rounded-[16px] flex-1 flex flex-col transition-all duration-300',
                isMinimized ? 'h-fit' : heightClass,
                hasClickAction && isHovered && 'border border-[#449995] cursor-pointer shadow-lg',
                wrapperClassName
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={handleWidgetClick}
        >
            <RefineModeContext.Consumer>
                {({ isRefineModeActive, setIsRefineModeActive }) => (
                    <>
                        <WidgetHeader
                            onRefineWidget={() => { setIsRefineModeActive(prev => !prev) }}
                            widget={widget}
                            title={displayTitle ?? widget?.title}
                            dateRangeLabel={aiDateRangeLabel}
                            description={widget.description}
                            widgetInfo={widget.widgetInfo}
                            iconType={dynamicIconType}
                            showDeleteButton={showDeleteButton}
                            onWidgetDelete={onWidgetDelete}
                            widgetData={data}
                            isHovered={isHovered}
                            isMinimized={isMinimized}
                            onMinimizeToggle={handleMinimizeToggle}
                            colSpan={colSpan}
                            allowInsights={allowInsights}
                            allowEdit={allowEdit}
                            onColumnReorder={handleColumnReorder}
                            onColumnChangeVisibility={handleColumnChangeVisibility}
                            onMetricChangeVisibility={handleMetricChangeVisibility}
                            widgetFiltersController={widgetFiltersController}
                            chartMetrics={chartMetrics}
                            hiddenMetrics={hiddenMetrics}
                            hiddenColumns={hiddenColumns}
                            showExportButton={showExportButton}
                            onExportClick={onExportClick}
                            isExportLoading={exportLoading}
                        />
                        {!isMinimized && widget && (
                            <>
                                <div className={classNames('relative transition-all', 'font-roboto', 'duration-300 ease-in-out min-h-32 h-full flex flex-col grow', { 'flex-1': widget.type === 'table' || widget.type !== 'competitor-table', 'min-h-[330px]': isRefineModeActive })}>
                                    {/* consumer-data manages its own loading overlay once data has loaded */}
                                    {loading && !(widget.type === 'consumer-data' && data) && (
                                        <div className="flex items-center justify-center flex-1 py-20 absolute h-full w-full z-100 top-0 left-0 bg-white/40">
                                            <LoadingSpinnerIcon />
                                        </div>
                                    )}
                                    {data ? renderWidget(widget, data, handleColumnReorder, showDeleteButton, onWidgetDelete, widgetFiltersController, colSpan, selectedDashboardCompareEnabled, hiddenColumns, hiddenMetrics, chartCategories, fetchWidgetData, loading, selectedDashboardDateRange, selectedDashboardCompareDateRange, setSortConsumerData, displayTitle, dealerContractedProducts) : (!loading && <NoDataView />)}
                                </div>
                            </>
                        )}
                    </>
                )}
            </RefineModeContext.Consumer>

        </div>
    );
};


const renderWidget = (
    widget: Widget,
    widgetData: any,
    onColumnReorder: (columns: IColumnDef[]) => void,
    showDeleteButton: boolean = true,
    onWidgetDelete: () => void,
    widgetFiltersController: ReturnType<typeof useWidgetFiltersController>,
    colSpan?: number,
    selectedDashboardCompareEnabled?: boolean,
    hiddenColumns?: Set<string>,
    hiddenMetrics?: Set<string>,
    chartCategories?: { categories: string[]; metricNames: string[] },
    fetchWidgetData?: (filters?: any, widgetOverride?: Widget) => Promise<any>,
    isLoading?: boolean,
    currentDateRange?: any,
    compareDateRange?: any,
    setSortConsumerData?: (sort: { field: string; direction: 'asc' | 'desc' }) => void,
    displayTitle?: string,
    dealerContractedProducts?: GetDealerContractedProductsResponse | null,
) => {
    const extractedData = widgetData?.widget?.data || widgetData?.data || widgetData;

    const columnVisibilityContext: ColumnVisibilityContext = {
        widgetProduct: widgetFiltersController?.selectedProducts?.map(p => p.data.productId) ?? [],
        widgetProductName: widgetFiltersController?.selectedProducts?.map(p => p.data.productName) ?? [],
        dealerProducts: dealerContractedProducts?.contractedProducts ?? [],
    };
    console.debug('[col-vis context]', { dealerProducts: columnVisibilityContext.dealerProducts, contractedProducts: dealerContractedProducts?.contractedProducts, mappedProducts: dealerContractedProducts?.mappedProducts });

    const hideColumnsList: string[] = Array.isArray((widget as any)?.metadata?.['hide-columns'])
        ? (widget as any).metadata['hide-columns']
        : [];
    const isColumnInHideList = (col: any): boolean => {
        if (!hideColumnsList.length) return false;
        return hideColumnsList.includes(col?.field)
            || hideColumnsList.includes(col?.metric)
            || hideColumnsList.includes(col?.header);
    };

    switch (widget.type) {
        case 'table':
            const tableData = {
                ...extractedData,
                columns: extractedData?.columns?.filter((col: any) => {
                    if (isColumnInHideList(col)) return false;
                    if (hiddenColumns?.has(col.header)) return false;
                    const visibilityRules = (widget as any)?.metadata?.['column-visibility'];
                    if (visibilityRules) {
                        return !isColumnHidden(col.field || col.metric || '', visibilityRules, columnVisibilityContext);
                    }
                    return true;
                }),
                metadata: {
                    ...extractedData?.metadata,
                    ...(widget as any)?.metadata,
                },
            };
            const metadata = (tableData?.metadata as ITableData['metadata']);
            const productLevel = (widget as any)?.metadata?.['product-level'] === true;
            const tablePolicy = getWidgetComparePolicy(widget, widgetFiltersController?.selectedPeriodItems);

            return (
                <div className="h-auto w-full">
                    {(!tablePolicy.skipPageCompare && selectedDashboardCompareEnabled) || (widgetFiltersController?.selectedCompareEnabled && widgetFiltersController?.selectedCompareDateRange) ? (
                        <TremorCompareTable
                            widgetFiltersController={widgetFiltersController}
                            data={tableData}
                            compare={widgetData?.compare}
                            filterDimension={metadata?.filter?.dimension?.metricName}
                            productLevel={productLevel}
                            widget={widget}
                            currentDateRange={widgetFiltersController?.selectedCompareEnabled && widgetFiltersController?.selectedDateRange ? widgetFiltersController.selectedDateRange : currentDateRange}
                            compareDateRange={widgetFiltersController?.selectedCompareEnabled ? widgetFiltersController?.selectedCompareDateRange : compareDateRange}
                        />
                    ) : (
                        <TremorTable
                            widgetFiltersController={widgetFiltersController}
                            data={tableData}
                            filterDimension={metadata?.filter?.dimension?.metricName}
                            productLevel={productLevel}
                            widget={widget}
                            onColumnReorder={onColumnReorder}
                        />
                    )}
                </div>
            );

        case 'consumer-data': {
            const tableData = {
                ...extractedData,
                columns: extractedData?.columns?.filter((col: any) => {
                    if (isColumnInHideList(col)) return false;
                    if (hiddenColumns?.has(col.header)) return false;
                    const visibilityRules = (widget as any)?.metadata?.['column-visibility'];
                    if (visibilityRules) {
                        return !isColumnHidden(col.field || col.metric || '', visibilityRules, columnVisibilityContext);
                    }
                    return true;
                }),
                metadata: {
                    ...extractedData?.metadata,
                    ...(widget as any)?.metadata,
                },
            };

            if (!fetchWidgetData) {
                return (
                    <div className="h-full flex items-center justify-center">
                        <p className="text-gray-500">Consumer data widget is missing fetch handler</p>
                    </div>
                );
            }

            return (
                <ConsumerDataWidget
                    widget={widget}
                    data={tableData}
                    isLoading={isLoading}
                    onSortChange={(field, direction) => {
                        setSortConsumerData?.({ field, direction });
                    }}
                    onApplyFilters={(params) => {
                        if (params?.dateRange?.from && params?.dateRange?.to) {
                            const offset = typeof params.offset === 'number' ? params.offset : 0;
                            widgetFiltersController.setSelectedDateRange({ from: params.dateRange.from, to: params.dateRange.to });
                            widgetFiltersController.setConsumerDataFilters({
                                marketingScores: params.marketingScores,
                                eventTypes: params.eventTypes,
                                eventTypesMode: params.eventTypesMode,
                                limit: params.limit,
                                offset,
                            });
                        }
                    }}
                />
            );
        }

        case 'chart':
            // Check if there's no data to display
            if (!extractedData || !extractedData.data || extractedData.data.length === 0) {
                return <NoDataView />;
            }

            const chartType = widget.chartType || widgetData?.chartType || extractedData?.chartType;

            switch (chartType) {
                case 'bar': return <BarChart widgetFiltersController={widgetFiltersController} data={extractedData} colors={widget.colors || undefined} compare={widgetData?.compare} selectedCompareEnabled={selectedDashboardCompareEnabled} metadata={(widget as any)?.metadata} colSpan={colSpan} segment={widget.service?.parameters?.segment} hiddenSeries={hiddenMetrics} />

                case 'stack-bar':
                    return (
                        <StackBarChart
                            data={extractedData}
                            colors={widget.colors || undefined}
                            valueFormatter={NumberUtils.createCurrencyFormatter(extractedData?.metrics)}
                            showLabels={false}
                            showSummaryCards={false}
                            indexKey={extractedData.indexKey}
                            stack={true}
                            segment={widget.service?.parameters?.segment}
                            metrics={extractedData?.metrics || widgetData?.widgetMetricsDefs?.map((m: any) => ({ ...m, metricName: m.name }))}
                            metadata={(widget as any)?.metadata}
                        />
                    );

                case 'line':
                    return (
                        <LineChart
                            data={extractedData}
                            colors={widget.colors || undefined}
                            widgetLevel={widget.service.parameters?.level || 'client'}
                            widget={widget}
                            compare={selectedDashboardCompareEnabled ? widgetData?.compare : undefined}
                            segment={widget.service?.parameters?.segment}
                            hiddenSeries={hiddenMetrics}
                            externalCategories={chartCategories}
                            widgetFiltersController={widgetFiltersController}
                        />
                    );

                case 'pie':
                case 'doughnut':
                    return (
                        <PieChart
                            widgetFiltersController={widgetFiltersController}
                            data={extractedData}
                            compare={selectedDashboardCompareEnabled ? widgetData?.compare : undefined}
                            colors={widget.colors || undefined}
                            valueFormatter={NumberUtils.createCurrencyFormatter(extractedData?.metrics)}
                            donut={chartType === 'doughnut'}
                            isVerticalLayout={colSpan !== undefined && colSpan <= 2}
                            widget={widget}
                            hiddenSeries={hiddenMetrics}
                        />
                    );
                case 'funnel':
                    const funnelData = extractedData?.chartType === 'funnel' ? extractedData : { data: extractedData?.data || extractedData };
                    return <Funnel data={funnelData} colors={widget.colors || undefined} widgetMetricsDefs={widgetData?.widgetMetricsDefs} hiddenSeries={hiddenMetrics} formatValue={NumberUtils.createCurrencyFormatter(extractedData?.metrics)} />;

                case 'stacked-bar-line':
                    return (
                        <StackedBarLine
                            data={extractedData}
                            colors={widget.colors || undefined}
                            valueFormatter={NumberUtils.createCurrencyFormatter(extractedData?.metrics)}
                            segment={widget.service?.parameters?.segment}
                            lineMetricLabel={widget.metadata?.["line-metric-label"]}
                            simpleBarLine={widget.metadata?.["simple-bar-line"]}
                            tripleStack={widget.metadata?.["triple-stack"]}
                        />
                    );
                case 'bubble':
                    const bubbleData = {
                        ...extractedData,
                        metadata: {
                            ...extractedData?.metadata,
                            ...(widget as any)?.metadata,
                        },
                    };
                    return <BubbleChart data={bubbleData} colors={widget.colors || undefined} hiddenSeries={hiddenMetrics} valueFormatter={NumberUtils.createCurrencyFormatter(extractedData?.metrics)} />;

                default:
                    return (
                        <div className="h-full flex items-center justify-center">
                            <p className="text-gray-500">Chart type &apos;{chartType}&apos; not yet supported</p>
                        </div>
                    );
            }

        case 'data-sources':
            return (
                <div className="w-full h-full">
                    <DataSources data={extractedData} />
                </div>
            );

        case 'iframe':
            return (
                <div className="w-full h-full">
                    <IframeWidget id={widget.id} title={displayTitle ?? widget.title} data={extractedData} metadata={(widget as any)?.metadata} />
                </div>
            );

        case 'social-impact-posts':
            return (
                <div className="w-full h-auto">
                    <SocialImpactPosts data={extractedData} />
                </div>
            );

        case 'competitor-table':
            const competitorMetadata = (extractedData?.metadata as ITableData['metadata']);
            const clientName = (widget as any)?.metadata?.clientName || widget.service?.parameters?.clientName;
            return (
                <div className="h-auto w-full">
                    <CompetitorTable
                        widgetFiltersController={widgetFiltersController}
                        data={extractedData}
                        filterDimension={competitorMetadata?.filter?.dimension?.metricName}
                        clientName={clientName}
                        pageLimit={widget?.metadata?.pageLimit}
                        hideHeader={widget?.metadata?.['hide-header']}
                    />
                </div>
            );

        case 'creative-gallery':
            return (
                <div className="w-full h-auto">
                    <CreativeGallery
                        data={extractedData}
                        metadata={(widget as any)?.metadata}
                    />
                </div>
            );
        case 'top-offers':
            return (
                <div className="w-full h-auto">
                    <TopOffers data={extractedData} />
                </div>
            );

        case 'table-map':
            return (
                <div className="w-full h-auto">
                    <TableMapOrganismWrapper />
                </div>
            );

        case 'audience-map':
            return (
                <div className="w-full h-auto">
                    <AudienceVisitorsOrganismWrapper />
                </div>
            );

        case 'combined':
            return (
                <div className="w-full h-full">
                    <CombinedWidget
                        widget={widget}
                        data={widgetData}
                        renderSection={(sectionWidget, sectionData) =>
                            renderWidget(
                                sectionWidget, sectionData, onColumnReorder, showDeleteButton,
                                onWidgetDelete, widgetFiltersController, colSpan,
                                selectedDashboardCompareEnabled, hiddenColumns, hiddenMetrics,
                                chartCategories, fetchWidgetData, isLoading,
                                currentDateRange, compareDateRange, setSortConsumerData,
                                undefined, dealerContractedProducts
                            )
                        }
                    />
                </div>
            );

        default:
            return (
                <div className="h-full flex items-center justify-center">
                    <p className="text-gray-500">Unsupported widget type: {widget.type}</p>
                </div>
            );
    }
};


export const WidgetFactory = React.memo(
    function WidgetFactory({ widget: originalWidget, onWidgetDelete, onMinimizeChange, onHiddenChange, colSpan, setDraggingDisabled, isDragging }: WidgetProps) {
        return (
            <WidgetFactoryWrapper widget={originalWidget} setDraggingDisabled={setDraggingDisabled}>
                <WidgetFactoryComponent
                    onWidgetDelete={onWidgetDelete}
                    onMinimizeChange={onMinimizeChange}
                    onHiddenChange={onHiddenChange}
                    colSpan={colSpan}
                    isDragging={isDragging}
                />
            </WidgetFactoryWrapper>
        );
    }
)